import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { track } from "@/lib/analytics";
import { useAuth } from "@/App";
import PaymentSuccessModal from "@/components/PaymentSuccessModal";
import { fmtDate } from "@/lib/practiceAccess";

/**
 * DodoReturnHandler - what happens when an international buyer comes back from
 * Dodo's checkout to /wallet?dodo_order=<ref>. We ask the server to confirm the
 * payment with Dodo (never trusting the redirect itself), credit it, and show the
 * same success screen as a Razorpay purchase. If Dodo is still confirming we
 * keep checking for about 20 seconds; the webhook and the watchdog finish the job
 * either way, so the buyer is never left unpaid-for.
 */
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export default function DodoReturnHandler() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, refreshTokens, updateTokens } = useAuth();
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const ref = p.get("dodo_order");
    const cancelled = p.get("dodo_cancelled");
    if (!ref && !cancelled) return undefined;
    const clean = () => navigate(location.pathname, { replace: true });
    if (cancelled) {
      toast.error("Payment cancelled");
      clean();
      return undefined;
    }
    if (!user) return undefined;   // wait for sign-in, then run
    let alive = true;
    (async () => {
      let done = false;
      for (let i = 0; i < 8 && alive && !done; i += 1) {
        try {
          const { data } = await api.post("/payments/dodo/verify", { order_ref: ref }, { timeout: 30000 });
          if (data?.ok && data.kind === "pass") {
            done = true;
            track("practice_pass_purchased", { provider: "dodo", plan: data.plan_key });
            window.dispatchEvent(new Event("formanti:practice-access-changed"));
            if (alive) toast.success(data.pass_until ? `You're in. Practice is open until ${fmtDate(data.pass_until)}.` : "You're in. Practice is open.");
          } else if (data?.ok) {
            done = true;
            track("purchase_completed", { provider: "dodo" });
            if (data.balance != null) updateTokens?.(data.balance);
            refreshTokens?.();
            if (alive) setSuccess({ tokens: data.tokens_credited, balance: data.balance, amount: data.amount_label });
          } else if (data?.state === "failed") {
            done = true;
            toast.error("The payment didn't complete. You were not charged.");
          }
        } catch { /* try again */ }
        if (!done) await sleep(2500);
      }
      if (!done && alive) {
        toast.message("Your payment is still confirming. Your purchase will appear within a few minutes.", { duration: 12000 });
      }
      if (alive) clean();
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, user?.id]);

  return (
    <PaymentSuccessModal
      open={!!success}
      onClose={() => setSuccess(null)}
      tokensCredited={success?.tokens}
      newBalance={success?.balance}
      packLabel={null}
      amountLabel={success?.amount}
    />
  );
}
